import { create } from 'zustand/react';
import type { TRegisterType, RegisterFormData } from '@/stores/register/types';
import type { ChangeEvent } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { createPickHook } from '../util';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import formatCPF from '@/utils/formatCPF';
import { toast } from '@/components/toast';
import { showOtpSendToasts } from '@/lib/otp-toasts';
import { sendPreRegistrationWhatsAppVerification } from '@/app/(auth)/actions';

const OTP_RESEND_COOLDOWN_MS = 30_000;

const initialState = {
  currentStep: 0,
  totalSteps: 6,
  loading: false,
  formData: {
    dateOfBirthTerms: true,
    avatarImage: '',
    phoneNumber: '',
    email: '',
    dateOfBirth: '',
    document: '',
    name: '',
    password: '',
    passwordConfirmation: '',
  },
  showPassword: false,
  isSendingWhatsAppCode: false,
  resendCooldownEnd: null as number | null,
  resendCooldownRemaining: 0,
  phoneNumber: undefined,
  pendingFormData: null,
  skippedOtp: false,
  userId: null,
  registrationToken: null,
  verificationTokenId: null,
};

export const RegisterStore = create<TRegisterType>((set, get) => {
  async function sendPreRegistrationOtp(
    phoneNumber: string,
    options: {
      onSuccess?: () => void;
      isResend?: boolean;
      channel?: 'whatsapp' | 'sms';
    } = {},
  ) {
    const formattedPhone = `55${phoneNumber.replace(/\D/g, '').trim()}`;
    const formData = new FormData();
    formData.append('phoneNumber', formattedPhone);
    if (options.channel) {
      formData.append('channel', options.channel);
    }

    set({
      isSendingWhatsAppCode: true,
    });

    try {
      const result = await sendPreRegistrationWhatsAppVerification(
        { status: 'idle' },
        formData,
      );

      set({ isSendingWhatsAppCode: false });
      handleOtpSendResult(result, {
        onSuccess: options.onSuccess,
        isResend: options.isResend,
        channel: options.channel,
      });
    } catch (error) {
      set({ isSendingWhatsAppCode: false });
      const action = options.isResend ? 'reenviar' : 'enviar';
      toast({
        type: 'error',
        description: `Erro ao ${action} código. Tente novamente.`,
      });
    }
  }

  function handleOtpSendResult(
    result: Awaited<ReturnType<typeof sendPreRegistrationWhatsAppVerification>>,
    options: {
      onSuccess?: () => void;
      isResend?: boolean;
      channel?: 'whatsapp' | 'sms';
    },
  ) {
    if (result.status === 'success') {
      set({
        resendCooldownEnd: Date.now() + OTP_RESEND_COOLDOWN_MS,
        resendCooldownRemaining: Math.ceil(OTP_RESEND_COOLDOWN_MS / 1000),
      });
      const toastOptions = {
        ...(options.isResend
          ? ({ verb: 'reenviado', action: 'reenviar' } as const)
          : {}),
        channel: options.channel ?? 'whatsapp',
      };
      showOtpSendToasts(result, toastOptions);
      options.onSuccess?.();
      return;
    }

    if (result.status === 'rate_limited') {
      const retryAfter = result.retryAfter ?? 30;
      set({
        resendCooldownEnd: Date.now() + retryAfter * 1000,
        resendCooldownRemaining: retryAfter,
      });
      toast({
        type: 'error',
        description: `Muitas tentativas. Aguarde ${retryAfter}s para reenviar.`,
      });
      return;
    }

    if (result.status === 'failed') {
      const action = options.isResend ? 'reenviar' : 'enviar';
      toast({
        type: 'error',
        description: `Erro ao ${action} código. Tente novamente.`,
      });
      return;
    }

    if (result.status === 'invalid_data') {
      toast({
        type: 'error',
        description: 'Número de telefone inválido.',
      });
    }
  }

  return {
    ...initialState,

    // Step control actions
    setCurrentStep: (step: number) => {
      const { totalSteps } = get();
      const validStep = Math.max(0, Math.min(step, totalSteps));
      set({ currentStep: validStep });
    },

    nextStep: () => {
      const { currentStep, totalSteps } = get();
      if (currentStep < totalSteps) {
        set({ currentStep: currentStep + 1 });
      }
    },

    previousStep: () => {
      const { currentStep, skippedOtp } = get();
      if (currentStep > 0) {
        // If we're on RegisterFormStep (step 2) and skipped OTP, go back to RegisterWelcomeStep (step 0)
        if (currentStep === 2 && skippedOtp) {
          set({ currentStep: 0 });
        } else {
          set({ currentStep: currentStep - 1 });
        }
      }
    },

    setLoading: (loading: boolean) => {
      set({ loading });
    },

    // Form data actions
    setFormData: (data: Partial<RegisterFormData>) => {
      set({ formData: data });
    },

    updateFormField: <K extends keyof RegisterFormData>(
      field: K,
      value: RegisterFormData[K],
    ) => {
      set((state) => ({
        formData: {
          ...state.formData,
          [field]: value,
        },
      }));
    },

    // UI state actions
    setShowPassword: (show: boolean) => {
      set({ showPassword: show });
    },

    toggleShowPassword: () => {
      set((state) => ({ showPassword: !state.showPassword }));
    },

    // Phone number actions
    setPhoneNumber: (phone: string | undefined) => {
      set({ phoneNumber: phone });
    },

    setPendingFormData: (data: FormData | null) => {
      set({ pendingFormData: data });
    },

    setSkippedOtp: (skipped: boolean) => {
      set({ skippedOtp: skipped });
    },

    setVerificationTokenId: (id: string | null) => {
      set({ verificationTokenId: id });
    },

    setUserId: (userId: string | null) => {
      set({ userId });
    },

    setRegistrationToken: (registrationToken: string | null) => {
      set({ registrationToken });
    },

    // Form handlers
    handleChangePhoneNumber: (event: ChangeEvent<HTMLInputElement>) => {
      const formatted = formatPhoneNumber(event.target.value);
      get().updateFormField('phoneNumber', formatted);
      return formatted;
    },

    handleChangeDocument: (event: ChangeEvent<HTMLInputElement>) => {
      const formatted = formatCPF(event.target.value);
      get().updateFormField('document', formatted);
    },

    handleChangeDate: (event: ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value.replace(/\D/g, '');

      if (value.length > 8) {
        value = value.slice(0, 8);
      }

      if (value.length >= 2) {
        const day = Number.parseInt(value.slice(0, 2));
        if (day > 31) {
          value = `31${value.slice(2)}`;
        } else if (day === 0) {
          value = `01${value.slice(2)}`;
        }
      }

      if (value.length >= 4) {
        const month = Number.parseInt(value.slice(2, 4));
        if (month > 12) {
          value = `${value.slice(0, 2)}12${value.slice(4)}`;
        } else if (month === 0) {
          value = `${value.slice(0, 2)}01${value.slice(4)}`;
        }
      }

      if (value.length >= 5) {
        value = value.replace(/(\d{2})(\d{2})(\d{0,4})/, '$1/$2/$3');
      } else if (value.length >= 3) {
        value = value.replace(/(\d{2})(\d{0,2})/, '$1/$2');
      }

      get().updateFormField('dateOfBirth', value);
    },

    // Form submission
    handleSendWhatsAppVerification: async (
      registerForm: UseFormReturn<RegisterFormData>,
      onSuccess?: () => void,
    ) => {
      const phoneNumber = registerForm.getValues('phoneNumber');

      if (!phoneNumber) {
        toast({
          type: 'error',
          description: 'Preencha o número de telefone',
        });
        return;
      }

      await sendPreRegistrationOtp(phoneNumber, { onSuccess });
    },

    resendWhatsAppCode: async () => {
      const { formData, resendCooldownRemaining } = get();
      const phoneNumber = formData.phoneNumber;

      if (resendCooldownRemaining > 0) return;

      if (!phoneNumber) {
        toast({
          type: 'error',
          description: 'Número de telefone não encontrado',
        });
        return;
      }

      await sendPreRegistrationOtp(phoneNumber, { isResend: true });
    },

    sendSmsCode: async () => {
      const { formData, resendCooldownRemaining } = get();
      const phoneNumber = formData.phoneNumber;

      if (resendCooldownRemaining > 0) return;

      if (!phoneNumber) {
        toast({
          type: 'error',
          description: 'Número de telefone não encontrado',
        });
        return;
      }

      await sendPreRegistrationOtp(phoneNumber, {
        isResend: true,
        channel: 'sms',
      });
    },

    tickResendCooldown: () => {
      const { resendCooldownEnd } = get();
      if (!resendCooldownEnd) return;

      const remaining = Math.max(
        0,
        Math.ceil((resendCooldownEnd - Date.now()) / 1000),
      );

      if (remaining <= 0) {
        set({ resendCooldownRemaining: 0, resendCooldownEnd: null });
      } else {
        set({ resendCooldownRemaining: remaining });
      }
    },

    // Reset
    reset: () => set(initialState),
  };
});

export const useRegisterStore = createPickHook<TRegisterType>(RegisterStore);

export default useRegisterStore;
